import {SearchAutocomplete} from '@app/search/search-autocomplete';
import clsx from 'clsx';
import {IconButton} from '@ui/buttons/icon-button';
import {Link, useLocation} from 'react-router';
import {Trans} from '@ui/i18n/trans';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {Menu, MenuTrigger} from '@ui/menu/menu-trigger';
import {Item} from '@ui/forms/listbox/item';
import {MenuIcon} from '@ui/icons/material/Menu';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import {message} from '@ui/i18n/message';
import {useTrans} from '@ui/i18n/use-trans';
import {MovieIcon} from '@ui/icons/material/Movie';
import {TvIcon} from '@ui/icons/material/Tv';
import {PeopleIcon} from '@ui/icons/material/People';
import {ArticleIcon} from '@ui/icons/material/Article';
import {LocalFireDepartmentIcon} from '@ui/icons/material/LocalFireDepartment';
import {ScheduleIcon} from '@ui/icons/material/Schedule';
import {CalendarTodayIcon} from '@ui/icons/material/CalendarToday';
import {StarIcon} from '@ui/icons/material/Star';
import {AutoAwesomeIcon} from '@ui/icons/material/AutoAwesome';
import {AiRecommendButton} from '@app/ai-recommend/ai-recommend-button';
import {LangSwitcher} from '@app/i18n/lang-switcher';
import {useLocale} from '@app/i18n/use-locale';
import {localizeTo} from '@app/i18n/localize-link';
import {LOCALE_META} from '@app/i18n/locale-config';
import {useAuth} from '@common/auth/use-auth';
import {NavbarAuthMenu} from '@common/ui/navigation/navbar/navbar-auth-menu';
import {UserAvatar} from '@common/auth/user-avatar';
import {Button} from '@ui/buttons/button';
import {LoginIcon} from '@ui/icons/material/Login';
import {DsThemeToggle} from '@app/design-system/ds-theme-toggle';
interface Props {
  position?: 'fixed' | 'relative';
}

interface HeaderNavLink {
  key: string;
  label: string;
  to: string;
  target?: '_blank' | '_self';
}

function normalizeHeaderLink(label: string, to: string): string {
  const locale = 'tr';
  const normalizedLabel = (label || '').toLowerCase();
  const normalizedTo = (to || '').toLowerCase();

  // Genre channels: /tur/{slug} or /tr/tur/{slug} → /tr/tur/{slug}
  if (normalizedTo.startsWith(`/${locale}/tur/`)) {
    const slug = normalizedTo.slice(`/${locale}/tur/`.length);
    return `/${locale}/tur/${slug}`;
  }
  if (normalizedTo.startsWith('/tur/')) {
    const [, , ...rest] = normalizedTo.split('/');
    const rawSlug = rest.join('/');
    const fixedSlug = rawSlug
      .replaceAll('muezik', 'muzik')
      .replaceAll('ü', 'u')
      .replaceAll('ö', 'o')
      .replaceAll('ı', 'i')
      .replaceAll('ğ', 'g')
      .replaceAll('ş', 's')
      .replaceAll('ç', 'c');
    return `/${locale}/tur/${fixedSlug}`;
  }

  // Listing pages: /filmler, /diziler, /oyuncular → /tr/...
  if (normalizedTo === '/filmler' || normalizedTo === `/${locale}/filmler`) return `/${locale}/filmler`;
  if (normalizedTo === '/diziler' || normalizedTo === `/${locale}/diziler`) return `/${locale}/diziler`;
  if (normalizedTo === '/oyuncular' || normalizedTo === `/${locale}/oyuncular`) return `/${locale}/oyuncular`;

  // News hub
  if (
    normalizedLabel.includes('haber') ||
    normalizedLabel.includes('news') ||
    normalizedTo === '/news' ||
    normalizedTo.startsWith('/news/') ||
    normalizedTo === '/kanal/haberler' ||
    normalizedTo.startsWith('/kanal/haberler/')
  ) {
    return `/${locale}/haberler`;
  }
  if (normalizedTo === '/haberler' || normalizedTo === `/${locale}/haberler`) return `/${locale}/haberler`;

  // Search
  if (normalizedTo === '/ara' || normalizedTo === `/${locale}/ara`) return `/${locale}/ara`;

  return to;
}

function getCategoryIcon(label: string, key: string) {
  const token = `${label} ${key}`.toLowerCase();
  if (token.includes('film')) return <MovieIcon size="sm" />;
  if (token.includes('dizi') || token.includes('series') || token.includes('tv'))
    return <TvIcon size="sm" />;
  if (
    token.includes('oyuncu') ||
    token.includes('people') ||
    token.includes('person')
  )
    return <PeopleIcon size="sm" />;
  if (token.includes('haber') || token.includes('news'))
    return <ArticleIcon size="sm" />;
  return <AutoAwesomeIcon size="sm" />;
}

function getTrendIcon(label: string) {
  const token = label.toLowerCase();
  if (token.includes('vizyon') || token.includes('trend'))
    return <LocalFireDepartmentIcon size="xs" />;
  if (token.includes('yak')) return <ScheduleIcon size="xs" />;
  if (token.includes('bug')) return <CalendarTodayIcon size="xs" />;
  if (token.includes('puan') || token.includes('iyi')) return <StarIcon size="xs" />;
  return <LocalFireDepartmentIcon size="xs" />;
}

export function MainNavbar({position = 'fixed'}: Props) {
  const menu = useCustomMenu('primary');
  const navigate = useNavigate();
  const {trans} = useTrans();
  const location = useLocation();
  const {user, isLoggedIn} = useAuth();
  const {current: currentLocale, active: activeLocales, switchTo: switchLocale} = useLocale();
  const showLocaleSwitcher = activeLocales.length >= 2;

  const categoryLinks: HeaderNavLink[] =
    menu?.items?.length
      ? menu.items.slice(0, 4).map(item => ({
          key: String(item.id),
          label: item.label,
          to: localizeTo(normalizeHeaderLink(item.label, item.action)),
          target: item.target,
        }))
      : [
          {key: 'films', label: 'Filmler', to: localizeTo('/tr/filmler'), target: '_self'},
          {key: 'series', label: 'Diziler', to: localizeTo('/tr/diziler'), target: '_self'},
          {key: 'people', label: 'Oyuncular', to: localizeTo('/tr/oyuncular'), target: '_self'},
          {key: 'news', label: 'Haberler', to: localizeTo('/tr/haberler'), target: '_self'},
        ];

  const trendLinks = [
    {label: 'Vizyondakiler', to: localizeTo('/tr/vizyondakiler')},
    {label: 'Trend Diziler', to: localizeTo('/tr/trend-diziler')},
    {label: 'Yakında', to: localizeTo('/tr/yakinda')},
    {label: 'Bugün Yayında', to: localizeTo('/tr/bugun-yayinda')},
    {label: 'En İyi Filmler', to: localizeTo('/tr/en-iyi-filmler')},
  ];

  const navigateTo = (to: string, target?: string) => {
    if (isAbsoluteUrl(to)) {
      window.open(to, target)?.focus();
    } else {
      navigate(to);
    }
  };

  return (
    <header
      className={clsx(
        'cine-main-header z-50 w-full flex-shrink-0 overflow-x-clip',
        position === 'fixed' && 'pt-[60px]',
      )}
    >
      {/* ── Top bar ── */}
      <div
        className={clsx(
          'cine-main-header__top cine-sticky-lock z-50 w-full',
          position === 'fixed' ? 'fixed left-0 right-0 top-0' : 'relative',
        )}
      >
        <div className="relative w-full border-b border-white/[0.06] bg-[#080c15]/96 shadow-[0_2px_32px_-8px_rgba(0,0,0,0.8)] backdrop-blur-2xl">
          {/* Golden accent line at very top */}
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent" />

          <div className="mx-auto flex h-[60px] max-w-[1680px] items-center gap-16 px-16 md:px-28">

            {/* Logo */}
            <Link
              to={localizeTo('/tr')}
              aria-label={trans(message('Anasayfaya git'))}
              className="group me-4 flex-shrink-0"
            >
              <Logo
                size="h-22 md:h-28 lg:h-30"
                color="light"
                className="transition-opacity duration-200 group-hover:opacity-85"
              />
            </Link>

            {/* Desktop nav links */}
            <nav
              aria-label={trans(message('Ana kategori menüsü'))}
              className="hidden items-center gap-2 lg:flex"
            >
              {categoryLinks.map(item => {
                const isActive = location.pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    target={item.target}
                    className={clsx(
                      'relative px-12 py-6 text-[15px] font-bold tracking-wide transition-colors duration-200',
                      isActive
                        ? 'text-yellow-300'
                        : 'text-white/75 hover:text-white',
                    )}
                  >
                    <Trans message={item.label} />
                    {/* Active underline */}
                    <span
                      className={clsx(
                        'absolute bottom-0 left-12 right-12 h-[2px] rounded-full bg-yellow-400 transition-all duration-200',
                        isActive ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </Link>
                );
              })}
            </nav>

            {/* Search — grows to fill space */}
            <div className="min-w-0 flex-1 lg:mx-12 lg:max-w-[480px]">
              <SearchAutocomplete className="[&>div]:h-38 [&>div]:items-center [&>div]:py-0 [&>div]:rounded-full [&>div]:border [&>div]:border-white/[0.10] [&>div]:bg-white/[0.07] [&>div]:ring-0 [&>div]:outline-none [&>div]:transition-all [&>div]:duration-200 [&>div:hover]:bg-white/[0.12] [&>div:focus-within]:border-yellow-400/40 [&>div:focus-within]:bg-white/[0.12] [&_input]:h-full [&_input]:my-0 [&_input]:py-0 [&_input]:text-[14px] [&_input]:font-medium [&_input]:leading-none [&_input]:text-white [&_input]:placeholder:text-white/50 [&_svg]:my-0 [&_svg]:self-center [&_svg]:text-white/60" />
            </div>

            {/* AI Recommend Button */}
            <div className="flex-shrink-0">
              <AiRecommendButton />
            </div>

            {/* Language switcher — hidden when fewer than 2 active locales */}
            <div className="hidden flex-shrink-0 md:block">
              <LangSwitcher variant="navbar" />
            </div>

            {/* Aydınlık/Karanlık tema toggle (D36) — koyu header'a uygun stil */}
            <div className="hidden flex-shrink-0 md:block">
              <DsThemeToggle className="border border-white/10 bg-white/[0.06] text-white/85 hover:bg-white/[0.12] hover:text-yellow-200" />
            </div>

            {/* Auth area: avatar menu (logged in) or login/register buttons (guest) */}
            <div className="flex flex-shrink-0 items-center gap-8">
              {isLoggedIn && user ? (
                <NavbarAuthMenu placement="bottom-end">
                  <button
                    className="cine-navbar-avatar-btn"
                    aria-label={trans(message('Hesabım'))}
                  >
                    <UserAvatar user={user} size="sm" circle withLink={false} />
                  </button>
                </NavbarAuthMenu>
              ) : (
                <>
                  {/* "Giriş" — secondary, sönük (md+ inline) */}
                  <Link
                    to="/giris"
                    className="hidden rounded-full border border-white/10 bg-white/[0.06] px-14 py-6 text-[13px] font-bold tracking-wide text-white/85 transition hover:bg-white/[0.12] hover:text-white md:inline-flex md:items-center md:gap-6"
                  >
                    <LoginIcon size="xs" />
                    <Trans message="Giriş" />
                  </Link>

                  {/* "Kayıt Ol" — PRIMARY CTA (md+: tam buton, gradient + sarı glow) */}
                  <Link
                    to="/kayit"
                    className={clsx(
                      'hidden md:inline-flex items-center justify-center',
                      // B3: proje Tailwind paletinde slate/yellow yok; arbitrary
                      // hex kullan (yoksa sınıflar üretilmez → beyaz metin/şeffaf zemin).
                      'rounded-full bg-gradient-to-b from-[#fde047] to-[#eab308]',
                      'px-16 py-7 text-[13.5px] font-extrabold tracking-wide text-[#0f172a]',
                      'shadow-[0_4px_14px_-3px_rgba(250,204,21,0.55)]',
                      'ring-1 ring-[#fde047]/30',
                      'transition-all duration-200',
                      'hover:from-[#fef08a] hover:to-[#facc15]',
                      'hover:shadow-[0_6px_20px_-3px_rgba(250,204,21,0.65)] hover:-translate-y-px',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#fde047] focus-visible:ring-offset-2 focus-visible:ring-offset-[#080c15]',
                      'active:translate-y-0 active:shadow-[0_2px_8px_-2px_rgba(250,204,21,0.4)]',
                    )}
                  >
                    <Trans message="Kayıt Ol" />
                  </Link>

                  {/* Mobile (<md): "Kayıt Ol" kompakt + "Giriş" icon-only */}
                  <Link
                    to="/kayit"
                    className={clsx(
                      'inline-flex items-center justify-center md:hidden',
                      // B3: arbitrary hex (palette'te yellow/slate yok)
                      'rounded-full bg-gradient-to-b from-[#fde047] to-[#eab308]',
                      'px-12 py-5 text-[12px] font-extrabold tracking-wide text-[#0f172a]',
                      'shadow-[0_3px_10px_-3px_rgba(250,204,21,0.5)]',
                      'ring-1 ring-[#fde047]/30',
                      'transition-all duration-200',
                      'active:scale-95',
                    )}
                  >
                    <Trans message="Kayıt Ol" />
                  </Link>
                  <Link
                    to="/giris"
                    aria-label={trans(message('Giriş'))}
                    className="inline-flex h-38 w-38 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-white/85 transition hover:bg-white/[0.12] hover:text-white md:hidden"
                  >
                    <LoginIcon size="sm" />
                  </Link>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <div className="ms-auto flex items-center lg:hidden">
              <MenuTrigger>
                <IconButton
                  className="h-38 w-38 rounded-full border border-white/10 bg-white/[0.07] text-white hover:bg-white/[0.14] hover:text-yellow-200"
                  aria-label={trans(message('Menüyü aç/kapat'))}
                >
                  <MenuIcon />
                </IconButton>
                <Menu>
                  {categoryLinks.map(item => (
                    <Item
                      value={item.to}
                      onSelected={() => navigateTo(item.to, item.target)}
                      key={item.key}
                      startIcon={
                        <span className="text-yellow-400/80">
                          {getCategoryIcon(item.label, item.key)}
                        </span>
                      }
                    >
                      <Trans message={item.label} />
                    </Item>
                  ))}
                  <div className="my-6 h-px bg-white/10" />
                  {trendLinks.map(item => (
                    <Item
                      value={item.to}
                      onSelected={() => navigateTo(item.to)}
                      key={item.label}
                      startIcon={
                        <span className="text-yellow-400/70">
                          {getTrendIcon(item.label)}
                        </span>
                      }
                    >
                      <Trans message={item.label} />
                    </Item>
                  ))}
                  {/* Language switcher (mobile) — flat items, only rendered when 2+ locales active */}
                  {showLocaleSwitcher && (
                    <>
                      <div className="my-6 h-px bg-white/10" />
                      {activeLocales.map(loc => {
                        const meta = LOCALE_META[loc];
                        return (
                          <Item
                            value={`lang-${loc}`}
                            onSelected={() => switchLocale(loc)}
                            key={`lang-${loc}`}
                            startIcon={
                              <span className="text-base leading-none" aria-hidden>
                                {meta.flag}
                              </span>
                            }
                            description={loc === currentLocale ? '✓' : undefined}
                          >
                            {meta.label}
                          </Item>
                        );
                      })}
                    </>
                  )}
                </Menu>
              </MenuTrigger>
            </div>
          </div>
        </div>
      </div>

      {/* ── Trend strip (desktop) ── */}
      <div className="cine-main-header__bottom hidden border-b border-white/[0.05] bg-[#060a12]/90 backdrop-blur-xl lg:block">
        <nav
          aria-label={trans(message('Trend etiketleri'))}
          className="mx-auto flex max-w-[1680px] items-center gap-4 overflow-x-auto px-28 py-[7px] scrollbar-none"
        >
          {trendLinks.map(item => {
            const isActive = location.pathname === item.to;
            return (
              <Link
                key={item.label}
                to={item.to}
                className={clsx(
                  'group flex flex-shrink-0 items-center gap-7 rounded-full px-14 py-[5px] text-[13px] font-bold tracking-wide transition-all duration-200',
                  isActive
                    ? 'bg-yellow-400/15 text-yellow-300 ring-1 ring-yellow-400/40'
                    : 'text-white/60 hover:bg-white/[0.07] hover:text-white/90',
                )}
              >
                <span
                  className={clsx(
                    'transition-colors duration-200',
                    isActive
                      ? 'text-yellow-400'
                      : 'text-white/40 group-hover:text-yellow-400/70',
                  )}
                >
                  {getTrendIcon(item.label)}
                </span>
                <Trans message={item.label} />
              </Link>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
